/**
 * Files the chart uses whose bytes this browser lost (W3-08; change request CR-W3-07-1; UX-065 / UX-066).
 *
 * A file can stay listed in the document while the browser dropped its stored bytes. Phira then gets a package
 * without it, so this is fatal (PH-02 for the song, PH-01 for any other file), but the analysis worker cannot see the
 * browser's storage. The problems service (main thread) therefore asks `persist.hasAsset` about every stored file the
 * chart uses (the song, the illustration and every file a line, note, effect, video or the unlock video uses; files
 * missing from an imported package are PH-01 / PH-02's own business) and adds one fatal problem per missing file, with
 * "Choose the file again" (`assets.replace`) as its fix. Ids are `PH-01:stored-<asset>` / `PH-02:stored-<asset>`.
 *
 * `StoredFilesProbe` keeps what the probes found for one open chart: it probes a file when the chart starts using it,
 * re-probes the missing ones when the chart's files change (choosing the same file again writes the bytes back
 * under the same id), on "Check now" and every `STORED_RECHECK_MS` while something is missing. A probe that fails
 * counts as present: a broken probe never raises a false alarm.
 */

import { FIELD } from '../../core/check/geometry';
import type { Problem } from '../../core/check/types';
import type { AssetId, ChartDoc, DocIndexes } from '../../core/model/types';
import type { Op } from '../../core/ops/types';
import { m } from './checker.messages';

/** Re-probe period of the missing files (choosing the same file again in Assets restores one). */
export const STORED_RECHECK_MS = 3000;

/** Problem id key of a missing stored file (`${rule}:stored-${asset}`). */
const KEY_PREFIX = 'stored-';

/** Stored files the chart uses (sorted): the song, the illustration and every used file; not package placeholders. */
export function storedFileIds(doc: Pick<ChartDoc, 'assets' | 'meta'>, idx: Pick<DocIndexes, 'assetUsers'>): AssetId[] {
  const out: AssetId[] = [];
  for (const a of doc.assets.values()) {
    if (a.missing === true) continue;
    const u = idx.assetUsers(a.id);
    const used = u.lines.length + u.notes.length + u.effects.length + u.videos.length + u.meta.length > 0;
    if (used || doc.meta.music === a.id || doc.meta.illustration === a.id) out.push(a.id);
  }
  return out.sort();
}

/** True for the problems this module makes (never sent by the analysis worker). */
export function isStoredMissingId(id: string): boolean {
  return id.startsWith(`PH-01:${KEY_PREFIX}`) || id.startsWith(`PH-02:${KEY_PREFIX}`);
}

/** The fatal problem of a used file whose stored bytes are missing (null when the chart does not hold it). */
export function storedMissingProblem(doc: Pick<ChartDoc, 'assets' | 'meta'>, id: AssetId): Problem | null {
  const asset = doc.assets.get(id);
  if (asset === undefined || asset.missing === true) return null;
  const song = doc.meta.music === id;
  const rule = song ? 'PH-02' : 'PH-01';
  return {
    id: `${rule}:${KEY_PREFIX}${id}`,
    rule,
    severity: 'fatal',
    category: 'phira',
    message: { key: m.storedMissing, params: { path: asset.path, song: song ? 'yes' : 'no' } },
    why: { key: m.storedMissingWhy },
    at: { asset: id, field: song ? FIELD.metaMusic : FIELD.assetPath },
    fixes: [{ label: { key: m.fixChooseAgain }, command: 'assets.replace', args: { asset: id } }],
  };
}

/** A committed change that may have put a file's bytes back (an asset record written or replaced). */
export function touchesAssets(ops: readonly Op[]): boolean {
  return ops.some((op) => 'tbl' in op && op.tbl === 'assets');
}

/** The persistence probe the service uses (`PersistClient.hasAsset`). */
export type HasAsset = (id: AssetId) => Promise<boolean>;

/**
 * What the probes found for one open chart. `onChange` runs when the set of missing files changes (the owner rebuilds
 * its problem list); `missing()` lists them among the files the chart uses now.
 */
export class StoredFilesProbe {
  private readonly hasAsset: HasAsset;
  private readonly onChange: () => void;
  /** Probe answers: false = missing. */
  private readonly known = new Map<AssetId, boolean>();
  private readonly inflight = new Set<AssetId>();
  private used: readonly AssetId[] = [];
  /** `used` joined (a cheap change test on every commit). */
  private usedKey = '';
  private disposed = false;

  constructor(hasAsset: HasAsset, onChange: () => void) {
    this.hasAsset = hasAsset;
    this.onChange = onChange;
  }

  /**
   * The chart's files may have changed: probes the files it starts using; `recheck` also probes the missing ones
   * again. Returns true when the missing files among the used ones changed (the owner's list depends on them).
   */
  update(doc: Pick<ChartDoc, 'assets' | 'meta'>, idx: Pick<DocIndexes, 'assetUsers'>, recheck: boolean): boolean {
    const used = storedFileIds(doc, idx);
    const key = used.join(',');
    let changed = false;
    if (key !== this.usedKey) {
      const before = this.missing().join(',');
      this.used = used;
      this.usedKey = key;
      changed = this.missing().join(',') !== before;
    }
    for (const id of used) {
      const k = this.known.get(id);
      if (k === undefined || (recheck && !k)) this.probe(id);
    }
    return changed;
  }

  /** Probes the missing files again (the periodic re-check). */
  recheckMissing(): void {
    for (const id of this.used) if (this.known.get(id) === false) this.probe(id);
  }

  /** Used files whose bytes are missing. */
  missing(): AssetId[] {
    return this.used.filter((id) => this.known.get(id) === false);
  }

  anyMissing(): boolean {
    return this.used.some((id) => this.known.get(id) === false);
  }

  dispose(): void {
    this.disposed = true;
  }

  private probe(id: AssetId): void {
    if (this.inflight.has(id)) return;
    this.inflight.add(id);
    const settle = (present: boolean): void => {
      this.inflight.delete(id);
      if (this.disposed) return;
      const before = this.known.get(id);
      this.known.set(id, present);
      // Only a change of what is missing matters to the owner (a first "present" answer changes nothing).
      if (before === undefined ? !present : before !== present) this.onChange();
    };
    let p: Promise<boolean>;
    try {
      p = this.hasAsset(id);
    } catch {
      settle(true);
      return;
    }
    void p.then(settle, () => {
      settle(true);
    });
  }
}
